import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { authLogin, ApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export interface StepUpReauthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Email de l'utilisateur connecté — vérifié via POST /api/auth/login. */
  email: string | undefined;
  /** Action sensible exécutée UNE FOIS le mot de passe confirmé. */
  onVerified: () => void;
  title?: string;
  description?: string;
}

/**
 * Ré-authentification « step-up » avant une action sensible (création de compte,
 * initiation de paiement…). Redemande le mot de passe de l'utilisateur CONNECTÉ et
 * le vérifie via l'endpoint de login (avec l'email courant). Succès → onVerified()
 * et fermeture ; échec → message d'erreur, la session reste intacte.
 *
 * Léger et réutilisable : overlay maison (PAS un Dialog Radix) pour rester
 * empilable au-dessus d'un Dialog existant sans conflit de focus/pointer-events.
 * Ne stocke pas les jetons renvoyés par le login (vérification seule) — la session
 * courante n'est pas modifiée, et un 401 de /api/auth/login ne la purge pas
 * (cf. isCredentialAuthPath dans api.ts).
 */
export default function StepUpReauthDialog({
  open,
  onOpenChange,
  email,
  onVerified,
  title = "Confirmez votre identité",
  description = "Cette action sensible nécessite de saisir à nouveau votre mot de passe.",
}: StepUpReauthDialogProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  // Repart d'un état propre à chaque ouverture.
  useEffect(() => {
    if (open) {
      setPassword("");
      setError(null);
      setVerifying(false);
    }
  }, [open]);

  if (!open) return null;

  const close = () => {
    onOpenChange(false);
  };

  const submit = async () => {
    if (!email) {
      setError("Email du compte introuvable. Reconnectez-vous.");
      return;
    }
    if (!password) {
      setError("Saisissez votre mot de passe.");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      await authLogin({ email, password });
      // Vérifié : on ferme puis on exécute l'action (sans conserver les jetons
      // renvoyés — la session courante reste la référence).
      onOpenChange(false);
      onVerified();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
        setError("Mot de passe incorrect.");
      } else {
        setError("Vérification impossible. Réessayez dans un instant.");
      }
      setVerifying(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="stepup-title"
      aria-describedby="stepup-desc"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4"
      // pointer-events explicite (inline) : reste cliquable même empilé au-dessus
      // d'un Dialog Radix modal, qui pose `pointer-events:none` en inline sur <body>.
      style={{ pointerEvents: "auto" }}
      data-testid="stepup-dialog"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !verifying) close();
      }}
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="rounded-t-lg bg-navy px-6 py-4">
          <h2
            id="stepup-title"
            className="flex items-center gap-2 font-display text-lg font-bold text-white"
          >
            <ShieldCheck className="h-5 w-5 text-gold shrink-0" /> {title}
          </h2>
          <p id="stepup-desc" className="mt-1 text-sm text-white/70">
            {description}
          </p>
        </div>
        <div className="space-y-3 px-6 py-4">
          <div>
            <Label htmlFor="stepup-password">Mot de passe</Label>
            <PasswordInput
              id="stepup-password"
              value={password}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !verifying) submit();
              }}
              data-testid="stepup-password"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600" role="alert" data-testid="stepup-error">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close} data-testid="stepup-cancel">
              Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={verifying || !password}
              className="bg-navy text-white hover:bg-navy/90"
              data-testid="stepup-confirm"
            >
              {verifying ? "Vérification…" : "Confirmer"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
